import { Routes, Route, NavLink, Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { Menu } from 'lucide-react'
import { Toaster } from '@/components/ui/sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RequireAuth } from '@/components/RequireAuth'
import { ThemeToggle } from '@/components/ThemeToggle'
import { LanguageToggle } from '@/components/LanguageToggle'
import { Brand } from '@/components/Brand'
import { useSession } from '@/lib/session'
import { cn } from '@/lib/utils'
import { useI18n, type MessageKey } from '@/i18n'
import { seo } from '@shared/seo.config'
import Home from '@/pages/Home'
import Settings from '@/pages/Settings'
import Account from '@/pages/Account'
import Login from '@/pages/Login'
import Register from '@/pages/Register'
import NotFound from '@/pages/NotFound'

// ─────────────────────────────────────────────────────────────────────────────
// APP SHELL + ROUTE TABLE.
//
// Adding a page = one file in src/pages/ plus one <Route> here. Wrap a page
// that needs an account in <RequireAuth>; an admin-only page in
// <RequireAuth admin>.
//
// ROUTE PATHS ARE ENGLISH AND STAY ENGLISH. They are identifiers, not copy:
// they end up in the sitemap, in every external link, and in this code. What
// the visitor READS is translated (src/i18n/), what the router MATCHES is not —
// so switching language never changes a URL and never breaks a shared link.
// ─────────────────────────────────────────────────────────────────────────────

const nav: { to: string; label: MessageKey; admin: boolean }[] = [
  { to: '/', label: 'nav.overview', admin: false },
  { to: '/settings', label: 'nav.settings', admin: true },
]

export default function App() {
  return (
    <div data-upbo-src="src/App.tsx:48" className="flex min-h-dvh flex-col">
      <SiteHeader />

      <main data-upbo-src="src/App.tsx:51" className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          {/* Settings changes configuration for the WHOLE website, so only its
              owner gets in. The server checks again at PUT /api/settings/seo —
              this wrapper only avoids showing a form whose Save button fails. */}
          <Route
            path="/settings"
            element={
              <RequireAuth admin>
                <Settings />
              </RequireAuth>
            }
          />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <Account />
              </RequireAuth>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <SiteFooter />

      <Toaster position="bottom-right" />
    </div>
  )
}

function SiteHeader() {
  const { user, loading, isAdmin } = useSession()
  const { t } = useI18n()
  const visible = nav.filter((item) => !item.admin || isAdmin)

  return (
    // RESPONSIVE BY CONSTRUCTION. The link row is desktop-only (`md:`); on a
    // phone the same `nav` array renders as a menu behind one button, so
    // adding a fifth or sixth entry never overflows the header. Keep this
    // shape when you add pages: edit `nav` above, not the markup here.
    <header data-upbo-src="src/App.tsx:96" className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
      <div data-upbo-src="src/App.tsx:97" className="mx-auto flex h-16 w-full max-w-6xl items-center gap-3 px-4 sm:px-6 md:gap-8">
        <Brand />

        <nav data-upbo-src="src/App.tsx:100" className="hidden items-center gap-1 md:flex">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'rounded-md px-3 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'bg-secondary text-secondary-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )
              }
            >
              {t(item.label)}
            </NavLink>
          ))}
        </nav>

        <div data-upbo-src="src/App.tsx:120" className="ml-auto flex items-center gap-1 sm:gap-2">
          <LanguageToggle />
          <ThemeToggle />
          {/* While `loading`, show NOTHING here. Rendering "Sign in" and then
              swapping it for the user's name is a flicker everybody sees. */}
          <div data-upbo-src="src/App.tsx:125" className="hidden items-center gap-1 md:flex sm:gap-2">
            {loading ? null : user ? (
              <Button asChild variant="ghost" size="sm">
                <Link to="/account" className="max-w-[10rem] truncate">{user.name || user.email}</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm">
                  <Link to="/login">{t('nav.signIn')}</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/register">{t('nav.signUp')}</Link>
                </Button>
              </>
            )}
          </div>

          {/* Phone / tablet: everything above, in one menu. */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label={t('nav.menu')}>
                <Menu />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {visible.map((item) => (
                <DropdownMenuItem key={item.to} asChild>
                  <Link to={item.to}>{t(item.label)}</Link>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {loading ? null : user ? (
                <DropdownMenuItem asChild>
                  <Link to="/account" className="truncate">{user.name || user.email}</Link>
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <Link to="/login">{t('nav.signIn')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/register">{t('nav.signUp')}</Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  )
}

function SiteFooter() {
  const { t } = useI18n()
  const { isAdmin } = useSession()

  const productLinks: { to: string; label: MessageKey }[] = [
    { to: '/', label: 'footer.product.overview' },
    { to: '/register', label: 'footer.product.createAccount' },
    { to: '/login', label: 'footer.product.signIn' },
  ]

  const resourceLinks = [
    { href: '/llms.txt', label: 'llms.txt' },
    { href: '/sitemap.xml', label: 'sitemap.xml' },
    { href: '/robots.txt', label: 'robots.txt' },
  ]

  const accountLinks: { to: string; label: MessageKey }[] = [
    { to: '/account', label: 'footer.account.account' },
    ...(isAdmin ? [{ to: '/settings', label: 'footer.account.settings' as MessageKey }] : []),
  ]

  return (
    <footer data-upbo-src="src/App.tsx:200" className="border-t border-border/60">
      <div data-upbo-src="src/App.tsx:201" className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6">
        <div data-upbo-src="src/App.tsx:202" className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div data-upbo-src="src/App.tsx:203">
            <Brand />
            <p data-upbo-src="src/App.tsx:205" className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {seo.description}
            </p>
          </div>

          <FooterColumn title={t('footer.product')}>
            {productLinks.map((link) => (
              <li data-upbo-src="src/App.tsx:212" key={link.to}>
                <Link to={link.to} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                  {t(link.label)}
                </Link>
              </li>
            ))}
          </FooterColumn>

          <FooterColumn title={t('footer.resources')}>
            {resourceLinks.map((link) => (
              <li data-upbo-src="src/App.tsx:222" key={link.href}>
                <a data-upbo-src="src/App.tsx:223" href={link.href} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                  {link.label}
                </a>
              </li>
            ))}
          </FooterColumn>

          <FooterColumn title={t('footer.account')}>
            {accountLinks.map((link) => (
              <li data-upbo-src="src/App.tsx:232" key={link.to}>
                <Link to={link.to} className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                  {t(link.label)}
                </Link>
              </li>
            ))}
          </FooterColumn>
        </div>

        <div data-upbo-src="src/App.tsx:241" className="mt-10 flex flex-col gap-4 border-t border-border/60 pt-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <span data-upbo-src="src/App.tsx:242">© {new Date().getFullYear()} {seo.siteName}</span>
          <span data-upbo-src="src/App.tsx:243">{t('footer.builtWith')}</span>
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div data-upbo-src="src/App.tsx:252">
      <p data-upbo-src="src/App.tsx:253" className="eyebrow">{title}</p>
      <ul data-upbo-src="src/App.tsx:254" className="mt-4 flex flex-col gap-2.5">{children}</ul>
    </div>
  )
}